<template>
  <Card>
    <CardHeader class="flex flex-row items-start justify-between gap-4">
      <CardTitle>
        {{ procedureTitle }}
      </CardTitle>
      <div class="flex items-center gap-2">
        <Button size="sm" variant="outline" :disabled="isSaveDisabled" @click="saveProcedure">
          {{ saveButtonLabel }}
        </Button>
        <Button size="sm" :disabled="isPublishDisabled" @click="publishProcedure">
          {{ publishButtonLabel }}
        </Button>
      </div>
    </CardHeader>
    <CardContent class="space-y-4">
      <p v-if="procedureStore.isLoading" class="text-sm text-muted-foreground">
        Loading procedure...
      </p>
      <template v-else>
        <p v-if="procedureGoal" class="text-sm text-muted-foreground">
          Goal: {{ procedureGoal }}
        </p>
        <p v-else class="text-sm text-muted-foreground">
          No goal defined yet.
        </p>

        <p v-if="!isDraftProcedure" class="text-sm text-muted-foreground">
          Only draft procedures can be edited or published.
        </p>

        <UmisisTiptapToolbar
          :actions="toolbarActions"
          :is-action-disabled="isToolbarActionDisabled"
          :is-action-active="isToolbarActionActive"
          toolbar-class="flex flex-wrap items-center gap-2"
          @action="runToolbarAction"
        />
        <input
          ref="imageFileInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleImageFileSelection"
        >
        <input
          ref="wordDocumentFileInput"
          type="file"
          accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          class="hidden"
          @change="handleWordDocumentFileSelection"
        >

        <div class="tiptap-editor rounded-md border border-input bg-transparent">
          <EditorContent :editor="editor" />
        </div>

        <p class="text-xs text-muted-foreground">
          {{ editorStatusLabel }}
        </p>

        <ProcedureImportedDocuments
          :procedure="procedure"
          @reimported-document="handleReimportedDocument"
        />
      </template>
    </CardContent>
  </Card>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import axios from 'axios'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../../../../resources/js/components/ui/card/index.js'
import UmisisTiptapToolbar from '../../../../../../../../resources/js/components/umisis/generic/UmisisTiptapToolbar.vue'
import { useApiAction } from '../../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import ProcedureImportedDocuments from '../../../components/business-systems/ProcedureImportedDocuments.vue'
import { useProcedureStore } from '../../../stores/procedures.js'
import { useTiptapToolbarActions } from '../../../composables/useTiptapToolbarActions.js'
import { toast } from 'vue-sonner'

const route = useRoute()
const router = useRouter()
const procedureStore = useProcedureStore()

const createEmptyEditorDocument = () => {
  return {
    type: 'doc',
    content: [{
      type: 'paragraph',
    }],
  }
}
const stringifyDocument = (document) => {
  return JSON.stringify(document)
}
const sanitizeTiptapMarks = (marks) => {
  if (!Array.isArray(marks)) {
    return undefined
  }

  const sanitizedMarks = marks
    .map((mark) => {
      if (!mark || typeof mark !== 'object' || Array.isArray(mark)) {
        return null
      }

      const type = typeof mark.type === 'string' ? mark.type : ''

      if (type === '') {
        return null
      }

      const sanitizedMark = {
        type,
      }

      if (mark.attrs && typeof mark.attrs === 'object' && !Array.isArray(mark.attrs)) {
        sanitizedMark.attrs = { ...mark.attrs }
      }

      return sanitizedMark
    })
    .filter((mark) => mark !== null)

  if (sanitizedMarks.length === 0) {
    return undefined
  }

  return sanitizedMarks
}
const sanitizeTiptapNode = (node) => {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return null
  }

  const nodeType = typeof node.type === 'string' ? node.type : ''

  if (nodeType === '') {
    return null
  }

  if (nodeType === 'text') {
    if (node.text === null || node.text === undefined) {
      return null
    }

    const sanitizedTextNode = {
      type: 'text',
      text: typeof node.text === 'string' ? node.text : String(node.text),
    }
    const sanitizedMarks = sanitizeTiptapMarks(node.marks)

    if (sanitizedMarks) {
      sanitizedTextNode.marks = sanitizedMarks
    }

    return sanitizedTextNode
  }

  const sanitizedNode = {
    type: nodeType,
  }

  if (node.attrs && typeof node.attrs === 'object' && !Array.isArray(node.attrs)) {
    sanitizedNode.attrs = { ...node.attrs }
  }

  if (Array.isArray(node.content)) {
    sanitizedNode.content = node.content
      .map((childNode) => sanitizeTiptapNode(childNode))
      .filter((childNode) => childNode !== null)
  }

  return sanitizedNode
}
const sanitizeProcedureDocument = (content) => {
  const normalizedContent = Array.isArray(content?.content) ? content.content : []
  const sanitizedContent = normalizedContent
    .map((node) => sanitizeTiptapNode(node))
    .filter((node) => node !== null)

  if (sanitizedContent.length === 0) {
    sanitizedContent.push({
      type: 'paragraph',
    })
  }

  return {
    type: 'doc',
    content: sanitizedContent,
  }
}
const normalizeProcedureContent = (content) => {
  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    return createEmptyEditorDocument()
  }

  if (content.type !== 'doc') {
    return createEmptyEditorDocument()
  }

  return sanitizeProcedureDocument(content)
}

const editorContentSnapshot = ref(stringifyDocument(createEmptyEditorDocument()))
const persistedContentSnapshot = ref(stringifyDocument(createEmptyEditorDocument()))
const imageFileInput = ref(null)
const wordDocumentFileInput = ref(null)
const uploadingImage = ref(false)
const importingWordDocument = ref(false)

const editor = useEditor({
  extensions: [StarterKit, Image],
  content: createEmptyEditorDocument(),
  editorProps: {
    attributes: {
      class: 'min-h-[16rem] w-full px-3 py-2 text-sm focus:outline-none',
    },
  },
  onUpdate: ({ editor: currentEditor }) => {
    editorContentSnapshot.value = stringifyDocument(currentEditor.getJSON())
  },
})

const businessSystemId = computed(() => Number(route.params.businessSystem ?? 0))
const procedureId = computed(() => Number(route.params.procedure ?? 0))
const procedure = computed(() => procedureStore.getItemById(procedureId.value))
const procedureTitle = computed(() => {
  if (!procedure.value?.title) {
    return 'Procedure'
  }

  return procedure.value.title
})
const procedureGoal = computed(() => {
  if (typeof procedure.value?.cached?.goal === 'string') {
    return procedure.value.cached.goal
  }

  return ''
})
const isDraftProcedure = computed(() => procedure.value?.status === 'draft')
const hasUnsavedChanges = computed(() => {
  return editorContentSnapshot.value !== persistedContentSnapshot.value
})
const saveButtonLabel = computed(() => {
  if (savingProcedure.value) {
    return 'Saving...'
  }

  return 'Save Draft'
})
const publishButtonLabel = computed(() => {
  if (publishingProcedure.value) {
    return 'Publishing...'
  }

  return 'Publish'
})
const editorStatusLabel = computed(() => {
  if (!isDraftProcedure.value) {
    return 'This procedure is not editable.'
  }

  if (uploadingImage.value) {
    return 'Uploading image...'
  }

  if (importingWordDocument.value) {
    return 'Importing Word document...'
  }

  if (savingProcedure.value || publishingProcedure.value) {
    return 'Saving changes...'
  }

  if (hasUnsavedChanges.value) {
    return 'Unsaved changes.'
  }

  return 'All changes saved.'
})
const isSaveDisabled = computed(() => {
  return uploadingImage.value
    || importingWordDocument.value
    || savingProcedure.value
    || publishingProcedure.value
    || procedureStore.isLoading
    || !isDraftProcedure.value
    || !editor.value
    || !hasUnsavedChanges.value
})
const isPublishDisabled = computed(() => {
  return uploadingImage.value
    || importingWordDocument.value
    || savingProcedure.value
    || publishingProcedure.value
    || procedureStore.isLoading
    || !isDraftProcedure.value
})

const resolveUploadedImageUrl = (upload) => {
  if (!upload || typeof upload !== 'object') {
    return ''
  }

  const candidateUrls = [
    upload.url_large,
    upload.url_medium,
    upload.url,
    upload.url_small,
  ]

  for (const candidateUrl of candidateUrls) {
    if (typeof candidateUrl === 'string' && candidateUrl.trim() !== '') {
      return candidateUrl
    }
  }

  return ''
}

const openImageFilePicker = () => {
  if (!isDraftProcedure.value || uploadingImage.value) {
    return
  }

  imageFileInput.value?.click()
}

const openWordDocumentFilePicker = () => {
  if (!isDraftProcedure.value
    || importingWordDocument.value
    || uploadingImage.value
    || savingProcedure.value
    || publishingProcedure.value
    || procedureStore.isLoading) {
    return
  }

  wordDocumentFileInput.value?.click()
}

const uploadImageAsset = async (file) => {
  const formData = new FormData()

  formData.append('file', file)
  formData.append('disk','private')

  const { data } = await axios.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  if (!Array.isArray(data) || data.length === 0) {
    return null
  }

  return data[0]
}

const handleImageFileSelection = async (event) => {
  if (!editor.value || !isDraftProcedure.value) {
    return
  }

  const selectedFile = event.target?.files?.[0]
  event.target.value = ''

  if (!selectedFile) {
    return
  }

  if (!selectedFile.type.startsWith('image/')) {
    toast.error('Only image files are allowed.')
    return
  }

  uploadingImage.value = true

  try {
    const upload = await uploadImageAsset(selectedFile)

    if (!upload || typeof upload !== 'object') {
      toast.error('Image upload failed.')
      return
    }

    if (!String(upload.mime_type ?? '').startsWith('image/')) {
      toast.error('Uploaded file is not an image.')
      return
    }

    const imageUrl = resolveUploadedImageUrl(upload)

    if (imageUrl === '') {
      toast.error('Image upload succeeded but no URL was returned.')
      return
    }

    editor.value
      .chain()
      .focus()
      .setImage({
        src: imageUrl,
        alt: selectedFile.name,
      })
      .run()
  } catch (error) {
    toast.error('Unable to upload the selected image.')
    console.error('Image upload failed:', error)
  } finally {
    uploadingImage.value = false
  }
}

const importWordDocument = async (file) => {
  return procedureStore.importProcedureFromWordDocument(procedureId.value, file)
}

const handleWordDocumentFileSelection = async (event) => {
  if (!editor.value || !isDraftProcedure.value) {
    return
  }

  const selectedFile = event.target?.files?.[0]
  event.target.value = ''

  if (!selectedFile) {
    return
  }

  importingWordDocument.value = true

  try {
    const importResult = await importWordDocument(selectedFile)
    const importedDocument = importResult?.document

    if (!importedDocument || typeof importedDocument !== 'object' || Array.isArray(importedDocument)) {
      toast.error('Import failed.')
      return
    }

    const normalizedContent = normalizeProcedureContent(importedDocument)

    editor.value.commands.setContent(normalizedContent)
    toast.success('Word document imported and attached to this procedure.')
  } catch (error) {
    toast.error('Unable to import the selected Word document.')
    console.error('Word document import failed:', error)
  } finally {
    importingWordDocument.value = false
  }
}

const handleReimportedDocument = (importedDocument) => {
  if (!editor.value || !isDraftProcedure.value) {
    return
  }

  if (!importedDocument || typeof importedDocument !== 'object' || Array.isArray(importedDocument)) {
    return
  }

  const normalizedContent = normalizeProcedureContent(importedDocument)

  editor.value.commands.setContent(normalizedContent)
}

const toolbarActions = useTiptapToolbarActions({
  onInsertImage: openImageFilePicker,
  canInsertImage: () => !uploadingImage.value,
  onImportFromWordDocument: openWordDocumentFilePicker,
  canImportFromWordDocument: () => !importingWordDocument.value
    && !uploadingImage.value
    && !savingProcedure.value
    && !publishingProcedure.value
    && !procedureStore.isLoading,
})

const syncEditorWithProcedure = (procedureToSync) => {
  if (!editor.value || !procedureToSync) {
    return
  }

  const normalizedContent = normalizeProcedureContent(procedureToSync.content)
  const normalizedContentSnapshot = stringifyDocument(normalizedContent)

  editor.value.commands.setContent(normalizedContent, false)
  persistedContentSnapshot.value = normalizedContentSnapshot
  editorContentSnapshot.value = normalizedContentSnapshot
}

const runToolbarAction = (action) => {
  if (!editor.value || !isDraftProcedure.value) {
    return
  }

  if (typeof action.onClick === 'function') {
    action.onClick()
    return
  }

  if (typeof action.execute !== 'function') {
    return
  }

  action.execute(editor.value.chain().focus()).run()
}

const isToolbarActionDisabled = (action) => {
  if (!editor.value || !isDraftProcedure.value) {
    return true
  }

  if (typeof action.canRun === 'function' && action.canRun(editor.value) === false) {
    return true
  }

  if (typeof action.execute !== 'function') {
    return false
  }

  return !action.execute(editor.value.can().chain().focus()).run()
}

const isToolbarActionActive = (action) => {
  if (!editor.value) {
    return false
  }

  return action.isActive(editor.value)
}

const { execute: executeSaveProcedure, loading: savingProcedure } = useApiAction((id, payload) => {
  return procedureStore.itemPatch(id, payload)
}, {
  onSuccess: (updatedProcedure) => {
    syncEditorWithProcedure(updatedProcedure)
    toast.success('Draft saved.')
  },
})

const { execute: executePublishProcedure, loading: publishingProcedure } = useApiAction((id) => {
  return procedureStore.publishProcedure(id)
}, {
  onSuccess: async (publishedProcedure) => {
    await router.push({
      name: 'ProcedureShowPage',
      params: {
        businessSystem: businessSystemId.value,
        procedure: publishedProcedure.id,
      },
    })
  },
})

const fetchProcedure = async () => {
  if (!Number.isFinite(procedureId.value) || procedureId.value <= 0) {
    return
  }

  const fetchedProcedure = await procedureStore.fetchItemById(procedureId.value)

  if (fetchedProcedure?.status === 'draft') {
    return
  }

  const draftProcedure = await procedureStore.startEditProcedure(procedureId.value)

  await router.replace({
    name: 'ProcedureEditPage',
    params: {
      businessSystem: businessSystemId.value,
      procedure: draftProcedure.id,
    },
  })
}

const saveProcedure = async () => {
  if (!editor.value || !isDraftProcedure.value || !hasUnsavedChanges.value) {
    return null
  }

  const content = editor.value.getJSON()
  const textContent = editor.value.getText().trim()

  return executeSaveProcedure(procedureId.value, {
    content,
    text_content: textContent === '' ? null : textContent,
  })
}

const publishProcedure = async () => {
  if (!isDraftProcedure.value) {
    return
  }

  if (hasUnsavedChanges.value) {
    const savedProcedure = await saveProcedure()

    if (!savedProcedure) {
      return
    }
  }

  await executePublishProcedure(procedureId.value)
}

onMounted(() => {
  fetchProcedure()
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

watch(editor, (currentEditor) => {
  if (!currentEditor || !procedure.value) {
    return
  }

  syncEditorWithProcedure(procedure.value)
}, {
  immediate: true,
})

watch(procedure, (currentProcedure) => {
  if (!currentProcedure) {
    return
  }

  syncEditorWithProcedure(currentProcedure)
})

watch(procedureId, () => {
  fetchProcedure()
})
</script>

<style scoped>
.tiptap-editor :deep(.ProseMirror ul),
.tiptap-editor :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
}

.tiptap-editor :deep(.ProseMirror ul) {
  list-style-type: disc;
}

.tiptap-editor :deep(.ProseMirror ol) {
  list-style-type: decimal;
}

.tiptap-editor :deep(.ProseMirror h2) {
  font-size: 1.25rem;
  font-weight: 600;
}

.tiptap-editor :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}
</style>
